import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function SignInModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl border border-border p-8 shadow-2xl animate-fade-in"
        style={{ background: '#0f0f1a' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
          aria-label="Close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl"
            style={{ background: 'linear-gradient(135deg, #e94560, #f5a623)' }}>
            📚
          </div>
          <h2 className="font-display text-2xl font-bold text-white mb-2">Sign in to LibreBooks</h2>
          <p className="text-gray-400 text-sm">Save books, track progress, sync across devices</p>
        </div>

        <div className="space-y-3 mb-6">
          {[
            { icon: (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              ), label: 'Continue with Google' },
            { icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ), label: 'Continue with Email' },
          ].map(({ icon, label }) => (
            <button key={label}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl
                         border border-border text-gray-300 font-medium text-sm
                         hover:border-accent/40 hover:bg-white/5 transition-all duration-200
                         cursor-not-allowed opacity-60"
              disabled
            >
              {icon}
              {label}
            </button>
          ))}
        </div>

        <div className="rounded-xl p-4 border border-amber-500/30"
          style={{ background: 'rgba(245,166,35,0.08)' }}>
          <p className="text-xs text-amber-400 text-center leading-relaxed">
            <strong>Coming soon!</strong> Full sign-in with Google &amp; email is being set up.
            Your bookmarks are saved locally in the meantime.
          </p>
        </div>
      </div>
    </div>
  );
}

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/search') setSearchQuery(params.get('q') || '');
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) { navigate(`/search?q=${encodeURIComponent(q)}`); setMobileMenuOpen(false); }
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/browse', label: 'Browse' },
    { to: '/search', label: 'Search' },
    {
      to: '/library', label: 'Library',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      ),
    },
  ];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      {showSignIn && <SignInModal onClose={() => setShowSignIn(false)} />}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-primary/95 backdrop-blur-md shadow-xl shadow-black/30 border-b border-border'
                 : 'bg-primary border-b border-border/50'
      }`}>
        <div className="page-container">
          <div className="flex items-center justify-between h-16 gap-4">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0" aria-label="LibreBooks Home">
              <span className="text-2xl">📚</span>
              <span className="font-display text-xl font-bold text-white group-hover:text-accent transition-colors">
                LibreBooks
              </span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-1 flex-shrink-0">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.to) ? 'bg-accent/20 text-accent' : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}>
                  {link.icon && <span className={isActive(link.to) ? 'text-accent' : 'text-gray-400'}>{link.icon}</span>}
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop search */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 flex-1 max-w-xs">
              <div className="relative w-full">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <input type="search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search books..."
                  className="w-full bg-surface border border-border rounded-lg pl-9 pr-4 py-2
                             text-sm text-gray-200 placeholder-gray-500
                             focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                             transition-all duration-200"
                  aria-label="Search books" />
              </div>
              <button type="submit"
                className="flex-shrink-0 bg-accent hover:bg-accent-light text-white text-sm
                           font-semibold px-4 py-2 rounded-lg transition-all duration-200
                           hover:shadow-lg hover:shadow-accent/30 active:scale-95">
                Go
              </button>
            </form>

            {/* Sign In — desktop */}
            <button type="button" onClick={() => setShowSignIn(true)}
              className="hidden md:inline-flex items-center gap-1.5 flex-shrink-0
                         px-4 py-2 rounded-lg text-sm font-semibold
                         border border-accent/50 text-accent
                         hover:bg-accent hover:text-white hover:border-accent
                         transition-all duration-200 active:scale-95">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Sign In
            </button>

            {/* Sign In — mobile (always visible) */}
            <button type="button" onClick={() => setShowSignIn(true)}
              className="md:hidden flex items-center gap-1 px-3 py-1.5 rounded-lg flex-shrink-0
                         text-xs font-semibold border border-accent/50 text-accent
                         hover:bg-accent hover:text-white transition-all duration-200 active:scale-95">
              Sign In
            </button>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5
                         transition-colors duration-200 flex-shrink-0"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen} aria-label="Toggle menu">
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border py-4 space-y-1 animate-fade-in">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.to) ? 'bg-accent/20 text-accent' : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}>
                  {link.icon && <span className={isActive(link.to) ? 'text-accent' : 'text-gray-400'}>{link.icon}</span>}
                  {link.label}
                </Link>
              ))}
              <form onSubmit={handleSearch} className="flex gap-2 pt-2">
                <input type="search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search books..."
                  className="flex-1 bg-surface border border-border rounded-lg px-4 py-2.5
                             text-sm text-gray-200 placeholder-gray-500
                             focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  aria-label="Search books" />
                <button type="submit"
                  className="bg-accent hover:bg-accent-light text-white text-sm font-semibold
                             px-5 py-2.5 rounded-lg transition-all duration-200 active:scale-95">
                  Search
                </button>
              </form>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

export default Navbar;
